import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { LicenseManagementService } from '../services/license-management.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { NetworkStateService } from '../shared/services/network-state.service';
import { OfflineDbService } from '../shared/services/offline-db.service';
import { checkLicenseOrTrialOrOffline } from '../shared/utils/license.util';
import { AuthService as SharedAuthService } from '../shared/services/auth.service';

// Routes that should bypass the license check (login/registrierung/profil/legal/Erklärseite)
const BYPASS_PREFIXES = [
  '/login',
  '/register',
  '/verify-email',
  '/welcome',
  '/license-request',
  '/profile',
  '/impressum',
  '/datenschutz',
  '/quellangaben',
  '/ueber-mich',
  '/disclaimer'
];

function hasActiveLicense(status: any): boolean {
  const lic = status?.active_license;
  if (!lic) return false;
  const validStatus = lic.status === 1;
  const notExpired = lic.expires_at ? new Date(lic.expires_at) >= new Date() : true;
  return validStatus && notExpired;
}

export const licenseGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const sharedAuth = inject(SharedAuthService);
  const licenseService = inject(LicenseManagementService);
  const network = inject(NetworkStateService);
  const offlineDb = inject(OfflineDbService);

  const url = state.url || '';
  const isNative = typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.();
  const tokenExists = sharedAuth.getToken();
  if (BYPASS_PREFIXES.some(prefix => url.startsWith(prefix))) {
    return true;
  }

  const user = auth.getCurrentUser();
  if (isNative) {
    console.log('[LicDbg][guard] url=', url, 'user=', user ? user.id : null, 'token=', !!tokenExists, 'offline=', network.isOffline());
  }
  // Wenn nicht eingeloggt, auf Landing-Page leiten
  if (!user) {
    return auth.fetchUserFromServer().pipe(
      map(fetched => {
        if (isNative) {
          console.log('[LicDbg][guard] fetched user=', fetched ? fetched.id : null);
        }
        if (fetched) {
          return true;
        }
        return router.parseUrl('/welcome');
      }),
      catchError(() => of(router.parseUrl('/welcome')))
    );
  }

  // Falls User vorhanden aber ohne created_at (Trial-Check braucht dieses Feld), Profil nachladen
  if (user && !user.created_at) {
    return auth.fetchUserFromServer().pipe(
      switchMap(fetchedUser => {
        const effectiveUser = fetchedUser || user;
        if (isNative) {
          console.log('[LicDbg][guard] refreshed user with created_at=', effectiveUser?.created_at || null);
        }
        return checkLicenseOrTrialOrOffline({
          user: effectiveUser,
          licenseService,
          network,
          offlineDb
        }).pipe(
          map((allowed) => {
            if (isNative) {
              console.log('[LicDbg][guard] allowed=', allowed);
            }
            return allowed ? true : router.parseUrl('/license-request');
          }),
          catchError((err) => {
            if (isNative) {
              console.log('[LicDbg][guard][error]', err);
            }
            return of(router.parseUrl('/license-request'));
          })
        );
      }),
      catchError((err) => {
        if (isNative) {
          console.log('[LicDbg][guard][error]', err);
        }
        return of(router.parseUrl('/license-request'));
      })
    );
  }

  return checkLicenseOrTrialOrOffline({
    user,
    licenseService,
    network,
    offlineDb
  }).pipe(
    map((allowed) => {
      if (isNative) {
        console.log('[LicDbg][guard] allowed=', allowed);
      }
      return allowed ? true : router.parseUrl('/license-request');
    }),
    catchError((err) => {
      if (isNative) {
        console.log('[LicDbg][guard][error]', err);
      }
      return of(router.parseUrl('/license-request'));
    })
  );
};
